/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React from 'react';

import { Colors } from 'src/common/ui/base/Colors';
import { Spacing } from 'src/common/ui/base/Spacing';
import { TypographyStyles } from 'src/common/ui/base/Typography';
import { BoulderIcon } from 'src/common/ui/components/BoulderIcon';

const containerStyles = css`
  align-items: center;
  display: flex;
  gap: ${Spacing.Unit2}px;
  justify-content: center;
  list-style: none;
  margin: ${Spacing.Unit3}px 0;
  padding: 0;
  ${TypographyStyles.body2};
`;

const sectionStyles = css`
  display: flex;
  line-height: ${Spacing.Unit4}px;

  & + & {
    margin-left: ${Spacing.Unit3}px;
  }
`;

const selectStyles = css`
  background-color: transparent;
  border: none;
  cursor: pointer;
  margin: 0;
  margin-left: ${Spacing.Unit1}px;
  padding: 0 ${Spacing.Unit1}px;
  height: ${Spacing.Unit4}px;
  line-height: ${Spacing.Unit4}px;
  ${TypographyStyles.body2};

  &:focus {
    outline: ${Colors.BlueSpruce} solid 2px;
  }
`;

const buttonStyles = css`
  background-color: transparent;
  border: none;
  color: ${Colors.CoolGray9};
  cursor: pointer;
  display: block;
  padding: ${Spacing.Unit2}px;
  transition: 0.1s ease-in-out;

  svg {
    display: block;
    position: relative;
    top: -1px;
  }

  &:focus {
    outline: ${Colors.BlueSpruce} solid 2px;
  }

  &:hover {
    background-color: ${Colors.CoolGray1};
    color: ${Colors.BlueSpruce};
  }

  &:disabled {
    background-color: transparent;
    color: ${Colors.CoolGray3};
    cursor: not-allowed;
  }
`;

/**
 * Pagination component for navigating through pages.
 */
export const Pagination = ({
  currentPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPage,
  rowsPerPageOptions = [10, 20, 50],
  totalRows,
}: {
  currentPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
  rowsPerPage: number;
  rowsPerPageOptions?: number[];
  totalRows?: number | null;
}) => {
  const totalPages = totalRows ? Math.ceil(totalRows / rowsPerPage) : null;
  const handlePageChange = (page: number) => {
    if (page >= 1 && page !== currentPage && (totalPages ? page <= totalPages : true)) {
      onPageChange(page);
    }
  };

  const startRow = (currentPage - 1) * rowsPerPage + 1;
  const endRow = totalRows
    ? Math.min(currentPage * rowsPerPage, totalRows)
    : currentPage * rowsPerPage;

  return (
    <menu css={containerStyles} data-testid="nds-component-pagination">
      <div css={sectionStyles}>
        Rows per page:{' '}
        <select
          css={selectStyles}
          value={rowsPerPage}
          onChange={e => onRowsPerPageChange(Number(e.target.value))}
        >
          {rowsPerPageOptions.map(rows => (
            <option key={rows} value={rows}>
              {rows}
            </option>
          ))}
        </select>
      </div>
      <div css={sectionStyles}>
        {startRow}-{endRow}
        {totalRows && ` of ${totalRows}`}
      </div>
      <div css={sectionStyles}>
        <button
          type="button"
          css={buttonStyles}
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage < 2}
        >
          <BoulderIcon icon="chevronLeft" size={18} />
        </button>
        <button
          type="button"
          css={buttonStyles}
          onClick={() => currentPage && handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <BoulderIcon icon="chevronRight" size={18} />
        </button>
      </div>
    </menu>
  );
};
